// =============================================
// helpers.js — Funciones utilitarias y constantes
// Extraído de App.js para mantener el código modular
// =============================================
import { createTheme } from '@mui/material';

/** ===== Branding / utilidades ===== */
export const placeholderImg = 'https://via.placeholder.com/1200x800?text=Sin+Imagen';
export const BRAND_LOGO_URL = "https://www.inflablesdasilva.com/wp-content/uploads/2026/02/WhatsApp-Image-2026-02-16-at-6.45.52-AM-1.jpeg";

// Cache-bust SOLO fotos del WordPress viejo (inflablesdasilva.com/wp-content): un bucle
// de redireccion ahi (18-sep-2026) quedo guardado como error hasta 7 dias en navegadores
// que lo alcanzaron a ver roto. Este sufijo fuerza a pedirla de nuevo una sola vez, sin
// tocar el archivo real ni las fotos de app.inflablesdasilva.com (esas nunca fallaron).
export const bustWpFoto = (url) => (typeof url === 'string' && /inflablesdasilva\.com\/wp-content\//i.test(url))
  ? url + (url.includes('?') ? '&' : '?') + '_fix=20260918'
  : url;

export const coverOf = (p) => {
  const imgs = p?.images || [];
  if (imgs.length) return imgs[Math.min(p.coverIndex ?? 0, imgs.length - 1)];
  return p?.link_foto || placeholderImg;
};
export const techPhotoOf = (p) => {
  const imgs = p?.images || [];
  if (!imgs.length) return undefined;
  const coverIdx = Math.min(p.coverIndex ?? 0, imgs.length - 1);
  const alt = imgs.find((_, i) => i !== coverIdx);
  return p.techPhotoUrl || alt || imgs[0];
};
export const noDownload = (e) => e.preventDefault();
export const imgProtectSx = { pointerEvents: 'none', userSelect: 'none', WebkitUserDrag: 'none' };

export const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  let videoId = null;
  const m1 = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (m1) videoId = m1[1];
  if (!videoId) {
    const m2 = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
    if (m2) videoId = m2[1];
  }
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

export const urlToDataURL = async (url) => {
  try {
    const res = await fetch(url, { mode: 'cors' });
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
  } catch { return null; }
};
export const genQRUrl = (text) => `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(text)}`;
export const PAYMENT_QR_URL = 'https://www.inflablesdasilva.com/wp-content/uploads/2026/02/WhatsApp-Image-2026-02-16-at-6.04.08-AM.jpeg';

// Proxy weserv.nl para evitar CORS en imágenes externas
export const weservProxy = (url) => {
  if (!url || url.startsWith('data:')) return url;
  const noProto = url.replace(/^https?:\/\//, '');
  return `https://images.weserv.nl/?url=${encodeURIComponent(noProto)}`;
};

// Carga una imagen como dataURL usando fetch con proxy weserv.nl (maneja GIF, JPEG, PNG y CORS)
export const loadImgDataURL = async (url) => {
  if (!url) return null;
  const proxied = weservProxy(url);
  try {
    const res = await fetch(proxied, { mode: 'cors' });
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
  } catch {
    // Fallback: img element + canvas
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = img.naturalWidth || 200;
          c.height = img.naturalHeight || 200;
          c.getContext('2d').drawImage(img, 0, 0);
          resolve(c.toDataURL('image/png'));
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = proxied;
    });
  }
};

// Geocodifica una dirección colombiana y devuelve { mapData, mapsUrl }
export const geocodeForMap = async (address, city) => {
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${address}, ${city}, Colombia`)}`;
  try {
    const q = encodeURIComponent(`${address}, ${city}, Colombia`);
    const geo = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`, {
      headers: { 'Accept-Language': 'es' }
    });
    const data = await geo.json();
    if (!data.length) return { mapData: null, mapsUrl };
    const lat = parseFloat(data[0].lat).toFixed(6);
    const lon = parseFloat(data[0].lon).toFixed(6);
    const mapImgUrl = `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lon}&zoom=15&size=500x220&markers=${lat},${lon},red-pushpin`;
    const mapData = await loadImgDataURL(mapImgUrl);
    return { mapData, mapsUrl, lat, lon };
  } catch { return { mapData: null, mapsUrl }; }
};

export const theme = createTheme({
  palette: {
    primary: { main: '#6366f1', light: '#818cf8', dark: '#4f46e5' },
    secondary: { main: '#8b5cf6', light: '#a78bfa', dark: '#7c3aed' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#64748b' },
  },
  typography: {
    fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif',
    h4: { fontWeight: 700 }, h5: { fontWeight: 600 }, h6: { fontWeight: 600 }
  },
  components: {
    MuiDrawer: { styleOverrides: { paper: { background: 'linear-gradient(180deg,#6366f1 0%,#8b5cf6 100%)', color: '#fff' } } },
    MuiButton: { styleOverrides: { root: { borderRadius: 8, textTransform: 'none', fontWeight: 500 } } },
    MuiCard: { styleOverrides: { root: { borderRadius: 12 } } }
  }
});

export const drawerWidth = 240;
